/* eslint-disable react-refresh/only-export-components */
import type {ReactElement} from 'react';
import {useCallback, useContext, useEffect, useState} from 'react';
import {Icon} from '@Components/icon-v2/icon';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {getAssetUrl} from '@Utils/s3.util';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {useStyleProperty} from '@EmailMakerReact/lib/email-maker-component-style-library';
import type {EmailMakerControl, EmailMakerControlProps} from '../../right-panel-registry.types';
import {createDescriptorControl} from '../create-descriptor-control';
import type {UploadButtonDescriptor} from './upload-button-control.types';
import styles from './upload-button-control.module.scss';

const DEFAULT_PREVIEW_ASSET = 'email-editor-upload-media-empty-placeholder.png';

interface UploadButtonControlInnerProps extends EmailMakerControlProps {
  descriptor: UploadButtonDescriptor;
}

/** Returns the cssValue unchanged when it is not a `url(...)` value. */
function extractUrlFromCssValue(cssValue: string): string {
  const match = /url\(["']?([^"')]+)["']?\)/.exec(cssValue);
  return match?.[1] ?? cssValue;
}

function UploadButtonControlInner({component, descriptor}: UploadButtonControlInnerProps): ReactElement {
  const {editor} = useContext(emailMakerContext);

  // The control instance is reused per panel slot, so subscribe to the style property rather than
  // seed once: this re-seeds on selection switch and on external writes (undo / setImageSrc).
  const liveRawValue = useStyleProperty(editor, component, descriptor.property ?? '');
  const livePreviewUrl = liveRawValue ? extractUrlFromCssValue(liveRawValue).replace(/"/g, '') : '';

  // The draft lets the upload callback show the picked URL before the async style write lands.
  const [previewUrl, setPreviewUrl] = useState<string>(livePreviewUrl);
  const hasPreview = previewUrl !== '';

  useEffect(() => {
    setPreviewUrl(livePreviewUrl);
  }, [livePreviewUrl]);

  const handleUploadClick = useCallback((): void => {
    if (!editor) {
      return;
    }

    descriptor.onUpload(editor, component, (url: string): void => {
      setPreviewUrl(url);
    });
  }, [editor, component, descriptor]);

  const handleClearClick = useCallback((): void => {
    if (!editor) {
      return;
    }

    descriptor.onClear(editor, component);
    setPreviewUrl('');
  }, [editor, component, descriptor]);

  const displayPreview = hasPreview ? previewUrl : getAssetUrl(DEFAULT_PREVIEW_ASSET);
  const label = window.i18next.t(descriptor.labelKey);

  function renderClearButton(): ReactElement | null {
    if (!hasPreview) {
      return null;
    }
    return (
      <div className={`btn clear-btn bg-neutral-0 border-s-darker radius-4 _cursor-pointer spacing-p-0 ${styles.deleteBtn}`}>
        <Icon ariaLabel={window.i18next.t('pmwjs_delete')} icon="icon-delete" isUsedAsButton onClick={handleClearClick} size={IconSize.SIZE_ICON_16} />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.labelRow}>
        <Text val={label} size={TextSize.MD} className={`style-manager-label ${styles.label}`} />
        <div className={styles.actions}>
          {renderClearButton()}
          <button
            aria-label={label}
            className={`btn flex-center bg-neutral-0 border-s-dark _cursor-pointer radius-8 spacing-p-1 hover-transition-all ${styles.uploadBtn}`}
            onClick={handleUploadClick}
            type="button"
          >
            <div className={`upload-preview radius-4 spacing-m-r-1 ${styles.preview}`} style={{backgroundImage: `url(${displayPreview})`}} />
            <Icon className="content-body" icon="icon-pencil" size={IconSize.SIZE_ICON_16} />
          </button>
        </div>
      </div>
    </div>
  );
}

export function makeUploadButtonControl(descriptor: UploadButtonDescriptor): EmailMakerControl {
  return createDescriptorControl(UploadButtonControlInner, descriptor);
}
